import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, finalize, mergeMap, shareReplay, tap } from 'rxjs/operators';
import {
  CT_HTTP_CACHE,
  CT_HTTP_CACHE_ABSOLUTE_EXPIRATION,
  CT_HTTP_CACHE_DURATION,
  CT_HTTP_CACHE_RELOAD,
} from '../../shared/interceptors/interceptor-context-tokens';
import { IHttpCacheResponse } from './http-cache-response';
import { HttpCacheService } from './http-cache.service';

export const CACHE_INSTANCE_HEADER = 'cache-instance';

@Injectable({ providedIn: 'root' })
export class HttpCacheInterceptor implements HttpInterceptor {
  cacheDurationDefault = 60;
  private _instanceId: string;
  // Map to track ongoing requests (in-memory semaphore)
  // Key: Request URL with params, Value: Observable of the HttpEvent
  private _pendingRequests = new Map<string, Observable<HttpEvent<any>>>();

  constructor(private readonly _httpCacheService: HttpCacheService) {}

  // Create observable to get the data from the embedded database
  private getCachedResponse(cacheKey: string): Observable<IHttpCacheResponse> {
    const cachedResponsePromise: Promise<IHttpCacheResponse> =
      this._httpCacheService.getById(cacheKey);
    return from(cachedResponsePromise);
  }

  getAbsoluteExpiration(req: HttpRequest<any>): Date | null {
    const absoluteExpiration = req.context.get(CT_HTTP_CACHE_ABSOLUTE_EXPIRATION);
    return absoluteExpiration ? dayjs(absoluteExpiration).toDate() : null;
  }

  getKey(req: HttpRequest<any>): string {
    if (!req.body) {
      return req.urlWithParams;
    }

    return `${req.urlWithParams}/${JSON.stringify(req.body)}`;
  }

  /**
   * Only for when the interceptor is provided at a non-global scope.
   */
  setInstanceId(instanceId: string): void {
    this._instanceId = instanceId;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let lastCachedResponse: IHttpCacheResponse = null;

    if (req.method !== 'GET' || !this.hasHttpCache(req)) {
      return next.handle(req);
    }

    // This is purely informative now.
    if (this._instanceId) {
      req = req.clone({
        setHeaders: {
          [CACHE_INSTANCE_HEADER]: this._instanceId,
        },
      });
    }

    const cacheKey = this.getKey(req);

    // This check MUST come before the Dexie check for concurrency control.
    const pending = this._pendingRequests.get(cacheKey);
    if (pending) {
      console.log(`[Cache Interceptor] Waiting for pending request: ${cacheKey}`);
      return pending; // Return the existing Observable
    }

    const request$ = this.getCachedResponse(cacheKey).pipe(
      mergeMap((cachedResponse: IHttpCacheResponse) => {
        if (cachedResponse) {
          lastCachedResponse = cachedResponse;

          if (this.isNotExpired(cachedResponse, req)) {
            const response = new HttpResponse({
              status: 200,
              body: cachedResponse.body,
            });

            return of(response);
          }
        }

        return next.handle(req).pipe(
          tap((event) => {
            if (event instanceof HttpResponse) {
              this.saveInCache(event, req);
            }
          }),
          // If any error occurs and a response in cache is available, return it.
          catchError((err, caught) => {
            if (err instanceof HttpErrorResponse && lastCachedResponse) {
              const response: HttpResponse<any> = new HttpResponse({
                status: 200,
                body: lastCachedResponse.body,
              });
              return of(response);
            }

            return throwError(err);
          }),
          finalize(() => {
            // Remove the request from the in-memory pending map
            console.log(`[Cache Interceptor] Finalizing pending request: ${cacheKey}`);
            this._pendingRequests.delete(cacheKey);
          }),
          shareReplay(1) // Share the single HTTP request execution among concurrent subscribers
        );
      })
    );

    // Store the Observable in the pending requests map (in-memory semaphore)
    this._pendingRequests.set(cacheKey, request$);

    return request$;
  }

  private hasHttpCache = (request: HttpRequest<any>): boolean => request.context.get(CT_HTTP_CACHE);

  private isNotExpired(cachedResponse: IHttpCacheResponse, request: HttpRequest<any>): boolean {
    const currentDate = dayjs();
    const expirationDate = dayjs(cachedResponse.expirationDate);
    const cacheAbsoluteExpiration = this.getAbsoluteExpiration(request);
    const absoluteExpiration = dayjs(cacheAbsoluteExpiration);

    return (
      (cacheAbsoluteExpiration && absoluteExpiration > currentDate) || expirationDate > currentDate
    );
  }

  private saveInCache(response: HttpResponse<any>, request: HttpRequest<any>): void {
    const body = response.body;
    const reload: boolean = request.context.get(CT_HTTP_CACHE_RELOAD);
    const cacheDuration = this.getCacheDuration(request);
    const cacheKey = this.getKey(request);

    const cachedResponse: IHttpCacheResponse = {
      url: cacheKey,
      body,
      lastModified: response.headers.get('last-modified'),
      creationDate: dayjs().toDate(),
      expirationDate: dayjs().add(cacheDuration, 'minute').toDate(),
      reload,
      instanceId: this._instanceId,
    };

    // Save everything in cache
    this._httpCacheService.put(cachedResponse);
  }

  private getCacheDuration = (request: HttpRequest<any>) =>
    Number(request.context.get(CT_HTTP_CACHE_DURATION) ?? this.cacheDurationDefault.toString());
}
